import { ApiMode, ApiTarget } from '../environment/api-environment.model';
import { SharedEnvironment } from '../environment/shared-environment.model';
import { resolveApiTarget } from './api-route-registry';

export function getApiMode(env: SharedEnvironment): ApiMode {
  return env.apiMode ?? 'monolith';
}

export function normalizeApiPath(path: string, env: SharedEnvironment): string {
  let normalized = path.replace(/^\/+/, '');

  if (env.enrollmentUrl) {
    const enrollmentPrefix = env.enrollmentUrl.replace(/^\/+/, '').replace(/\/+$/, '');
    if (enrollmentPrefix && normalized.startsWith(`${enrollmentPrefix}/`)) {
      normalized = normalized.slice(enrollmentPrefix.length + 1);
    } else if (normalized === enrollmentPrefix) {
      normalized = '';
    }
  }

  if (normalized.startsWith('api/')) {
    normalized = normalized.slice(4);
  }

  return normalized.replace(/^\/+/, '').replace(/\/+$/, '');
}

export function extractPathFromUrl(url: string, env: SharedEnvironment): string | null {
  try {
    const parsed = new URL(url);
    return normalizeApiPath(parsed.pathname, env);
  } catch {
    return null;
  }
}

export function isMonolithHost(hostname: string, env: SharedEnvironment): boolean {
  try {
    return new URL(env.adminApiUrl).hostname === hostname;
  } catch {
    return false;
  }
}

export function isSplitApiHost(hostname: string, env: SharedEnvironment): boolean {
  if (!env.apiUrls) {
    return false;
  }

  const hosts = Object.values(env.apiUrls).map((baseUrl) => {
    try {
      return new URL(baseUrl).hostname;
    } catch {
      return '';
    }
  });

  return hosts.includes(hostname);
}

export function getSplitBaseUrl(target: ApiTarget, env: SharedEnvironment): string | null {
  const baseUrl = env.apiUrls?.[target];
  return baseUrl ? baseUrl.replace(/\/+$/, '') : null;
}

export function buildSplitUrl(normalizedPath: string, target: ApiTarget, env: SharedEnvironment): string | null {
  const baseUrl = getSplitBaseUrl(target, env);
  if (!baseUrl) {
    return null;
  }

  const path = normalizedPath.replace(/^\/+/, '');
  return path ? `${baseUrl}/${path}` : baseUrl;
}

export function resolveRewrittenUrl(url: string, env: SharedEnvironment): string {
  if (getApiMode(env) !== 'split' || !env.apiUrls) {
    return url;
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }

  const normalizedPath = normalizeApiPath(parsed.pathname, env);
  if (!normalizedPath) {
    return url;
  }

  const onMonolith = isMonolithHost(parsed.hostname, env);
  const onSplit = isSplitApiHost(parsed.hostname, env);
  if (!onMonolith && !onSplit) {
    return url;
  }

  const target = resolveApiTarget(normalizedPath);
  if (!target) {
    // Deliberately fatal. Falling back to the monolith hid stale copies of the generated
    // route table until the monolith stopped serving the route in production.
    throw new Error(
      `[ApiRouting] No split API mapping for path "${normalizedPath}". Either regenerate ` +
        `src/mappings in k12-api-enrollment and copy the route files across, or call this ` +
        `endpoint through the matching API client from @k12/app-services.`
    );
  }

  const rewritten = buildSplitUrl(normalizedPath, target, env);
  if (!rewritten) {
    return url;
  }

  const rewrittenUrl = new URL(rewritten);
  rewrittenUrl.search = parsed.search;
  rewrittenUrl.hash = parsed.hash;
  return rewrittenUrl.toString();
}

export function resolveSignalRBase(env: SharedEnvironment): string {
  if (getApiMode(env) === 'split' && env.apiUrls?.infrastructure) {
    return env.apiUrls.infrastructure.replace(/\/+$/, '');
  }

  return env.adminApiUrl.replace(/\/+$/, '');
}
